import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { CalendarDays, Clock, ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { SEO } from "@/lib/seo";
import { waLink, DEFAULT_WA_MESSAGE } from "@/lib/whatsapp";
import { SERVICES } from "@/data/services";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("es-CO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 sm:px-6" data-testid="blog-post-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-24 rounded bg-slate-200" />
          <div className="h-8 w-full rounded bg-slate-200" />
          <div className="aspect-[16/9] rounded-3xl bg-slate-100" />
          <div className="h-3 w-full rounded bg-slate-200" />
          <div className="h-3 w-5/6 rounded bg-slate-200" />
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6" data-testid="blog-post-not-found">
        <h1 className="font-display text-3xl font-bold text-slate-900">Artículo no encontrado</h1>
        <p className="mt-4 text-slate-600">El artículo que buscas no existe o fue movido.</p>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white hover:bg-brand-dark" data-testid="blog-post-back-link">
          <ArrowLeft size={16} /> Volver al blog
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`blog-post-${slug}`}>
      <SEO title={`${post.title} | Blog Colors Limpieza`} description={post.excerpt} />

      <article className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-brand" data-testid="blog-post-back">
              <ArrowLeft size={16} /> Volver al blog
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-sunset-light px-3 py-1 text-xs font-semibold text-sunset-dark">{post.category}</span>
              <span className="flex items-center gap-1.5 text-xs text-slate-400">
                <CalendarDays size={13} /> {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-400">
                <Clock size={13} /> {post.read_time} de lectura
              </span>
            </div>
            <h1 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl">
              {post.title}
            </h1>
          </Reveal>

          <Reveal delay={0.1}>
            <img
              src={post.image}
              alt={post.title}
              className="mt-8 aspect-[16/9] w-full rounded-3xl object-cover shadow-lg"
              data-testid="blog-post-image"
            />
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 space-y-6" data-testid="blog-post-content">
              {post.content.map((block, i) =>
                block.type === "h2" ? (
                  <h2 key={i} className="pt-4 font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                    {block.text}
                  </h2>
                ) : (
                  <p key={i} className="text-base leading-relaxed text-slate-600">
                    {block.text}
                  </p>
                )
              )}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-12 rounded-3xl bg-gradient-to-br from-brand to-brand-dark p-8 text-center md:p-10" data-testid="blog-post-cta">
              <h2 className="font-display text-xl font-bold text-white sm:text-2xl">
                ¿Listo para una limpieza profunda?
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/85">
                Cotiza tu servicio a domicilio en Medellín en menos de 2 minutos.
              </p>
              <a
                href={waLink(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-sunset px-7 py-3.5 text-sm font-bold text-white shadow-[0_8px_25px_-5px_rgba(255,162,89,0.4)] transition-colors hover:bg-sunset-dark"
                data-testid="blog-post-whatsapp-cta"
              >
                <MessageCircle size={17} /> Cotizar por WhatsApp
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-12">
              <h2 className="font-display text-lg font-bold text-slate-900">Nuestros servicios</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/servicios/${s.slug}`}
                    className="group rounded-2xl border border-slate-100 bg-slate-50 p-5 transition-all hover:border-brand/30 hover:bg-brand-light/50"
                    data-testid={`blog-post-service-${s.slug}`}
                  >
                    <p className="font-display text-sm font-bold text-slate-900 group-hover:text-brand-dark">{s.name}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand">
                      Ver servicio <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
